// /settings — accessibility preferences + timezone for reminder
// scheduling. Both persisted via PATCH /api/me/accessibility and
// PATCH /api/me/timezone. The accessibility theme is applied to the
// <html> element so it takes effect immediately and survives
// navigation.

import { useEffect, useState } from 'react';
import { SiggyAvatar } from '../components/SiggyAvatar';
import { SeniorButton } from '../components/senior/SeniorButton';
import { useAuthedFetch } from '../hooks/useAuthedFetch';
import { useMe } from '../hooks/useMe';
import { usePushSubscription } from '../hooks/usePushSubscription';
import { AppPage } from '../layouts/AppPage';
import { LOCALE_LABEL, LOCALE_REVIEWED, type Locale, useLocale } from '../lib/i18n';

type FontSize = 'base' | 'lg' | 'xl';

const FONT_SIZE_LABEL: Record<FontSize, string> = {
  base: 'Normal',
  lg: 'Larger',
  xl: 'Largest',
};

const FONT_SIZE_PX: Record<FontSize, string> = {
  base: '18px',
  lg: '22px',
  xl: '26px',
};

// Common US timezones surfaced first; user can type anything valid.
const COMMON_TZS = [
  'America/Los_Angeles',
  'America/Denver',
  'America/Phoenix',
  'America/Chicago',
  'America/New_York',
  'America/Anchorage',
  'Pacific/Honolulu',
];

// Format an IANA timezone for the senior-friendly dropdown:
// "America/Los_Angeles" -> "Los Angeles (Pacific)" when we can infer
// a friendly label, else just "Los Angeles". Keeps the underlying
// option value as the IANA id so the API contract is unchanged.
const FRIENDLY_TZ_LABELS: Record<string, string> = {
  'America/Los_Angeles': 'Los Angeles (Pacific)',
  'America/Denver': 'Denver (Mountain)',
  'America/Phoenix': 'Phoenix (Arizona)',
  'America/Chicago': 'Chicago (Central)',
  'America/New_York': 'New York (Eastern)',
  'America/Anchorage': 'Anchorage (Alaska)',
  'Pacific/Honolulu': 'Honolulu (Hawaii)',
};

function formatTimezoneLabel(tz: string): string {
  if (FRIENDLY_TZ_LABELS[tz]) return FRIENDLY_TZ_LABELS[tz];
  return tz
    .replace(/_/g, ' ')
    .replace(/^America\//, '')
    .replace(/^Pacific\//, '');
}

function applyAccessibility(
  fontSize: FontSize,
  highContrast: boolean,
  reducedMotion: boolean,
): void {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  html.style.setProperty('--font-size-base', FONT_SIZE_PX[fontSize]);
  html.classList.toggle('theme-high-contrast', highContrast);
  html.classList.toggle('theme-reduced-motion', reducedMotion);
}

export function Settings(): JSX.Element {
  const authedFetch = useAuthedFetch();
  const { me } = useMe();
  const [fontSize, setFontSize] = useState<FontSize>('base');
  const [highContrast, setHighContrast] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [timezone, setTimezone] = useState('America/Los_Angeles');
  const [wakeTime, setWakeTime] = useState('08:00');
  const [bedTime, setBedTime] = useState('22:00');
  const [phone, setPhone] = useState('');
  const [phoneCountry, setPhoneCountry] = useState('US');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [hearingConcern, setHearingConcern] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!me) return;
    setFontSize((me.accessibility.fontSize as FontSize | undefined) ?? 'base');
    setHighContrast(Boolean(me.accessibility.highContrast));
    setReducedMotion(Boolean(me.accessibility.reducedMotion));
    setHearingConcern(Boolean(me.accessibility.hearingConcern));
    setTimezone(me.timezone || 'America/Los_Angeles');
    setWakeTime(me.wakeTime || '08:00');
    setBedTime(me.bedTime || '22:00');
    setPhone(me.phoneE164 ?? '');
  }, [me]);

  useEffect(() => {
    applyAccessibility(fontSize, highContrast, reducedMotion);
  }, [fontSize, highContrast, reducedMotion]);

  const saveAccessibility = async (next: {
    fontSize?: FontSize;
    highContrast?: boolean;
    reducedMotion?: boolean;
    hearingConcern?: boolean;
  }) => {
    setBusy(true);
    try {
      await authedFetch('/api/me/accessibility', {
        method: 'PATCH',
        body: JSON.stringify(next),
      });
      setSavedAt(Date.now());
    } finally {
      setBusy(false);
    }
  };

  const saveTimezone = async () => {
    setBusy(true);
    try {
      await authedFetch('/api/me/timezone', {
        method: 'PATCH',
        body: JSON.stringify({ timezone }),
      });
      setSavedAt(Date.now());
    } finally {
      setBusy(false);
    }
  };

  const saveSchedule = async () => {
    setBusy(true);
    try {
      await authedFetch('/api/me/schedule', {
        method: 'PATCH',
        body: JSON.stringify({ wakeTime, bedTime }),
      });
      setSavedAt(Date.now());
    } finally {
      setBusy(false);
    }
  };

  const savePhone = async () => {
    setBusy(true);
    setPhoneError(null);
    try {
      const res = await authedFetch('/api/me/phone', {
        method: 'PATCH',
        body: JSON.stringify({
          phone: phone.trim() || null,
          country: phoneCountry === 'OTHER' ? undefined : phoneCountry,
        }),
      });
      const json = (await res.json()) as { ok: boolean; error?: { message: string } };
      if (!json.ok) {
        setPhoneError(json.error?.message ?? 'Could not save phone number.');
        return;
      }
      setSavedAt(Date.now());
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppPage title="Settings" back={{ to: '/account', label: 'Account' }}>
      <section className="food-intro">
        <SiggyAvatar size={48} />
        <div>
          <p className="food-intro__eyebrow">Make it yours</p>
          <p className="food-intro__text">
            Set your text size, contrast, and time zone. We use your time zone to send reminders at
            the right moment.
          </p>
        </div>
      </section>

      <section className="account-section">
        <h3 className="account-section__title">Text size</h3>
        <div className="symptom-row__scale" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          {(['base', 'lg', 'xl'] as FontSize[]).map((size) => (
            <button
              key={size}
              type="button"
              className={`symptom-pill${fontSize === size ? ' is-active' : ''}`}
              onClick={() => {
                setFontSize(size);
                void saveAccessibility({ fontSize: size });
              }}
              disabled={busy}
            >
              {FONT_SIZE_LABEL[size]}
            </button>
          ))}
        </div>
      </section>

      <section className="account-section">
        <h3 className="account-section__title">High contrast</h3>
        <div className="account-row">
          <span id="high-contrast-label" className="account-row__label">
            Boost contrast for easier reading
          </span>
          <button
            type="button"
            aria-labelledby="high-contrast-label"
            className={`toggle${highContrast ? ' is-on' : ''}`}
            onClick={() => {
              const next = !highContrast;
              setHighContrast(next);
              void saveAccessibility({ highContrast: next });
            }}
            aria-pressed={highContrast}
          >
            <span className="toggle__dot" />
          </button>
        </div>
        <div className="account-row">
          <span className="account-row__label">Reduce motion</span>
          <button
            type="button"
            aria-label="Reduce motion"
            className={`toggle${reducedMotion ? ' is-on' : ''}`}
            onClick={() => {
              const next = !reducedMotion;
              setReducedMotion(next);
              void saveAccessibility({ reducedMotion: next });
            }}
            aria-pressed={reducedMotion}
          >
            <span className="toggle__dot" />
          </button>
        </div>
        {/* Round-3 fix #5: Robert + James (denial demographic) reacted to
            seeing health-condition toggles in default-visible Settings as
            the app pre-judging them. Tucked behind a disclosure so users
            who want it can find it; users who don't won't see it. */}
        <details
          style={{ marginTop: 8 }}
          // Persist open state if the user has already enabled the
          // toggle — they're past the denial step.
          open={hearingConcern}
        >
          <summary
            style={{
              cursor: 'pointer',
              fontSize: 15,
              fontWeight: 600,
              padding: '8px 0',
              color: 'var(--color-text-secondary)',
            }}
          >
            More options
          </summary>
          <div className="account-row" style={{ marginTop: 8 }}>
            <span className="account-row__label">
              Hearing matters to me
              <span
                style={{ display: 'block', fontSize: 14, color: 'var(--color-text-secondary)' }}
              >
                {hearingConcern
                  ? "We're showing extra warnings for ibuprofen, aspirin, loop diuretics, and certain antibiotics."
                  : 'Turn this on if you have tinnitus or hearing loss. We will raise extra warnings for medicines that can affect hearing.'}
              </span>
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  color: hearingConcern ? 'var(--color-success)' : 'var(--color-text-tertiary)',
                }}
                aria-hidden="true"
              >
                {hearingConcern ? 'ON' : 'OFF'}
              </span>
              <button
                type="button"
                aria-label={`Flag medicines that can affect hearing, currently ${hearingConcern ? 'ON' : 'OFF'}`}
                className={`toggle${hearingConcern ? ' is-on' : ''}`}
                onClick={() => {
                  const next = !hearingConcern;
                  setHearingConcern(next);
                  void saveAccessibility({ hearingConcern: next });
                }}
                aria-pressed={hearingConcern}
              >
                <span className="toggle__dot" />
              </button>
            </span>
          </div>
        </details>
      </section>

      <section className="account-section">
        <h3 className="account-section__title">Phone for text reminders</h3>
        <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', margin: '0 0 12px' }}>
          Add a mobile number to get a text message reminder when it's time for a medicine. Works
          worldwide: US numbers can be 10 digits; international numbers need the country code with a
          plus sign (e.g. +91 98765 43210). Reply STOP to opt out at any time. Standard message and
          data rates may apply.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>Country</span>
            <select
              value={phoneCountry}
              onChange={(e) => setPhoneCountry(e.target.value)}
              aria-label="Phone number country"
              style={{
                padding: '14px 16px',
                fontSize: 18,
                minHeight: 48,
                border: '1.5px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                background: 'var(--color-surface-primary)',
              }}
            >
              <option value="US">United States (+1)</option>
              <option value="CA">Canada (+1)</option>
              <option value="GB">United Kingdom (+44)</option>
              <option value="IN">India (+91)</option>
              <option value="MX">Mexico (+52)</option>
              <option value="PH">Philippines (+63)</option>
              <option value="OTHER">Other (use + sign)</option>
            </select>
          </label>
          <label
            style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 200 }}
          >
            <span style={{ fontSize: 16, fontWeight: 600 }}>Mobile number</span>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={
                phoneCountry === 'US' || phoneCountry === 'CA'
                  ? '(555) 555-1234'
                  : '+91 98765 43210'
              }
              aria-label="Mobile number"
              style={{
                padding: '14px 16px',
                fontSize: 18,
                minHeight: 48,
                border: '1.5px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
              }}
            />
          </label>
        </div>
        {phoneError ? (
          <p style={{ marginTop: 8, color: 'var(--color-critical)', fontSize: 16 }}>{phoneError}</p>
        ) : null}
        <div style={{ marginTop: 12 }}>
          <SeniorButton variant="primary" onClick={() => void savePhone()} disabled={busy}>
            Save number
          </SeniorButton>
        </div>
      </section>

      <LanguageSection />
      <section className="account-section">
        <h3 className="account-section__title">Other languages</h3>
        <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', margin: '0 0 12px' }}>
          Tell us which language we should translate next. We will email you when it is ready. Top
          requests we already track:
        </p>
        {/* Round-3 fix #11: Rosa, Carlos, Ahmed, Mei all flagged the
            "(soon)" wall. Until Dr. Marlee Grounds signs off on the
            bilingual review (CLAUDE.md gate), surface the top three
            requests as one-tap chips so the user can register interest
            without typing. */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {['Mandarin', 'Arabic', 'Vietnamese', 'Tagalog', 'Hindi'].map((lang) => (
            <button
              key={lang}
              type="button"
              className="symptom-pill"
              style={{ minHeight: 40, padding: '6px 14px' }}
              onClick={() => {
                void authedFetch('/api/me/language-request', {
                  method: 'POST',
                  body: JSON.stringify({ language: lang }),
                }).catch(() => null);
                setSavedAt(Date.now());
              }}
            >
              + {lang}
            </button>
          ))}
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            const langWanted = String(fd.get('lang') ?? '').trim();
            if (!langWanted) return;
            void authedFetch('/api/me/language-request', {
              method: 'POST',
              body: JSON.stringify({ language: langWanted }),
            }).catch(() => null);
            setSavedAt(Date.now());
            (e.currentTarget as HTMLFormElement).reset();
          }}
          style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
        >
          <input
            type="text"
            name="lang"
            placeholder="Something else? Type it here..."
            aria-label="Language you want"
            style={{
              flex: 1,
              minWidth: 200,
              padding: '12px 14px',
              fontSize: 16,
              border: '1.5px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
            }}
          />
          <SeniorButton variant="primary">Tell Siggy</SeniorButton>
        </form>
      </section>

      <section className="account-section">
        <h3 className="account-section__title">Time zone</h3>
        <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', margin: '0 0 12px' }}>
          Reminders fire at the times you set, in this time zone.
        </p>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 16, fontWeight: 600 }}>Pick yours</span>
          <select
            aria-label="Time zone"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            style={{
              padding: '14px 16px',
              fontSize: 18,
              minHeight: 48,
              border: '1.5px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-surface-primary)',
            }}
          >
            {COMMON_TZS.includes(timezone) ? null : (
              <option value={timezone}>{formatTimezoneLabel(timezone)}</option>
            )}
            {COMMON_TZS.map((tz) => (
              <option key={tz} value={tz}>
                {formatTimezoneLabel(tz)}
              </option>
            ))}
          </select>
        </label>
        <div style={{ marginTop: 12 }}>
          <SeniorButton variant="primary" onClick={() => void saveTimezone()} disabled={busy}>
            Save time zone
          </SeniorButton>
        </div>
      </section>

      <section className="account-section">
        <h3 className="account-section__title">Your day</h3>
        <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', margin: '0 0 12px' }}>
          When a medicine label says "in the morning" or "at bedtime", we use these times for
          reminders.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>Wake time</span>
            <input
              type="time"
              value={wakeTime}
              onChange={(e) => setWakeTime(e.target.value)}
              style={{
                padding: '12px 14px',
                fontSize: 18,
                border: '1.5px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
              }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 16, fontWeight: 600 }}>Bedtime</span>
            <input
              type="time"
              value={bedTime}
              onChange={(e) => setBedTime(e.target.value)}
              style={{
                padding: '12px 14px',
                fontSize: 18,
                border: '1.5px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
              }}
            />
          </label>
        </div>
        <div style={{ marginTop: 12 }}>
          <SeniorButton variant="primary" onClick={() => void saveSchedule()} disabled={busy}>
            Save day schedule
          </SeniorButton>
        </div>
      </section>

      <PushNotificationsSection />

      {savedAt ? (
        <section className="account-section">
          <p style={{ color: 'var(--color-success)', margin: 0 }}>Saved.</p>
        </section>
      ) : null}
    </AppPage>
  );
}

function PushNotificationsSection(): JSX.Element | null {
  const { state, enable, disable, test, error } = usePushSubscription();
  const isIos =
    typeof navigator !== 'undefined' &&
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !(window as unknown as { MSStream?: unknown }).MSStream;
  const isStandalone =
    typeof window !== 'undefined' &&
    // iOS uses navigator.standalone; others use display-mode: standalone.
    (window.matchMedia?.('(display-mode: standalone)').matches ||
      (navigator as unknown as { standalone?: boolean }).standalone === true);

  if (state.state === 'not-configured') return null;

  return (
    <section className="account-section">
      <h3 className="account-section__title">Reminders on this device</h3>
      <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', margin: '0 0 12px' }}>
        Get a tap on your phone or computer when it is time for a medicine. We will keep sending the
        email reminder either way.
      </p>

      {state.state === 'unsupported' ? (
        <div
          style={{
            padding: 12,
            border: '1.5px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-surface-secondary)',
            fontSize: 16,
          }}
        >
          This browser does not support push notifications. On an iPhone or iPad, tap the share icon
          and choose "Add to Home Screen" to install MedReview, then come back here.
        </div>
      ) : null}

      {state.state === 'denied' ? (
        <div
          style={{
            padding: 12,
            border: '1.5px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-surface-secondary)',
            fontSize: 16,
          }}
        >
          Notifications were turned off for this site. To turn them back on, open your browser
          settings, find this site, and allow notifications.
        </div>
      ) : null}

      {isIos && !isStandalone && state.state !== 'unsupported' && state.state !== 'granted' ? (
        <div
          style={{
            padding: 12,
            border: '1.5px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-surface-secondary)',
            fontSize: 16,
            marginBottom: 12,
          }}
        >
          To get push notifications on iPhone or iPad, first tap the share icon in Safari and choose
          "Add to Home Screen". Then open MedReview from the home screen and come back to this page.
        </div>
      ) : null}

      {state.state === 'prompt' ? (
        <SeniorButton variant="primary" onClick={() => void enable()}>
          Turn on push reminders
        </SeniorButton>
      ) : null}

      {state.state === 'granted' ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <SeniorButton onClick={() => void test()}>Send a test push</SeniorButton>
          <SeniorButton onClick={() => void disable()}>Turn off on this device</SeniorButton>
        </div>
      ) : null}

      {error ? (
        <p style={{ marginTop: 12, color: 'var(--color-critical)', fontSize: 16 }}>{error}</p>
      ) : null}
    </section>
  );
}

function LanguageSection(): JSX.Element {
  const { locale, setLocale } = useLocale();
  const options: Locale[] = ['en', 'es'];
  return (
    <section className="account-section">
      <h3 className="account-section__title">Language</h3>
      <p style={{ fontSize: 16, color: 'var(--color-text-secondary)', margin: '0 0 12px' }}>
        Pick your language. We start with English; Spanish unlocks once Dr. Marlee Grounds, PharmD
        signs off on the bilingual review (target this summer). Other languages below.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {options.map((l) => {
          const isLocked = !LOCALE_REVIEWED[l];
          const isActive = locale === l;
          return (
            <button
              key={l}
              type="button"
              onClick={() => {
                if (!isLocked) setLocale(l);
              }}
              disabled={isLocked && !isActive}
              aria-pressed={isActive}
              className={`symptom-pill${isActive ? ' is-active' : ''}`}
              style={{ minHeight: 48, minWidth: 120, opacity: isLocked ? 0.6 : 1 }}
              title={isLocked ? 'Coming soon (pharmacist review pending)' : undefined}
            >
              {LOCALE_LABEL[l]}
              {isLocked ? ' (soon)' : ''}
            </button>
          );
        })}
      </div>
    </section>
  );
}
